(() => {
  const TILE_SELECTOR = "[data-showroom-tile]";

  const formatMoney = (cents) => {
    const theme = window.theme || {};
    if (typeof window.formatMoney === "function") {
      return window.formatMoney(cents, theme.moneyFormat);
    }
    return "$" + (Number(cents || 0) / 100).toFixed(2);
  };

  const isCoffeeProduct = (product) => {
    const type = String((product && product.type) || "").toLowerCase();
    return type === "coffee" || type === "green coffee";
  };

  const poundsFromVariant = (variant, product) => {
    if (!variant) return 0;
    const title = [variant.option1, variant.option2, variant.option3, variant.title].filter(Boolean).join(" ");
    if (/sample/i.test(title)) return 0;
    const withUnit = String(title).toLowerCase().match(/(\d+(?:\.\d+)?)\s*(?:#|lbs?|pounds?)/);
    if (withUnit) return parseFloat(withUnit[1]);
    if (!isCoffeeProduct(product)) return 0;
    const grams = Number(variant.grams || variant.weight || 0);
    if (grams >= 50) return grams / 453.59237;
    return 0;
  };

  const formatPackPriceWithPerPound = (variant, product) => {
    const cents = Number((variant && variant.price) || 0);
    const pack = formatMoney(cents);
    const lbs = poundsFromVariant(variant, product);
    if (!(lbs > 0) || !(cents > 0)) return pack;
    return `${pack} (${formatMoney(cents / lbs)}/lb)`;
  };

  const parseProduct = (root) => {
    const jsonEl = root.querySelector("[data-showroom-tile-product]");
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (error) {
      console.error("Showroom product tile: invalid product JSON", error);
      return null;
    }
  };

  const getVariant = (product, id) => {
    if (!product || !product.variants || !id) return null;
    const want = Number(id);
    return product.variants.find((variant) => Number(variant.id) === want) || null;
  };

  const setButtonState = (button, labelEl, { disabled, label, loading, awaiting }) => {
    if (!button) return;
    button.disabled = Boolean(disabled) || Boolean(loading);
    button.classList.toggle("cursor-not-allowed", button.disabled && !awaiting);
    button.classList.toggle("showroom-quick-buy__atc--awaiting", Boolean(awaiting));
    if (labelEl && label) {
      labelEl.textContent = label;
    }
  };

  const initTile = (root) => {
    if (!root || root.dataset.showroomTileReady === "true") return;
    root.dataset.showroomTileReady = "true";

    const product = parseProduct(root);
    const hasBagSize = root.getAttribute("data-has-bag-size") === "true";
    const bagSizeRange = root.getAttribute("data-bag-size-range") || "Select bag size";
    const addLabel = root.getAttribute("data-add-to-cart") || "Add to cart";
    const soldOutLabel = root.getAttribute("data-sold-out") || "Sold out";
    const addingLabel = root.getAttribute("data-adding") || "Adding";

    const priceEl = root.querySelector("[data-showroom-price]");
    const selectEl = root.querySelector("[data-showroom-variant]");
    const qtyInput = root.querySelector("[data-showroom-qty]");
    const atcButton = root.querySelector("[data-showroom-atc]");
    const atcLabel = root.querySelector("[data-showroom-atc-label]");
    const errorEl = root.querySelector("[data-showroom-error]");
    const form = root.querySelector("form.quick-buy-product-form");

    const setPrice = (text) => {
      if (priceEl && text) priceEl.textContent = text;
    };

    const setError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message || "";
    };

    const applyVariant = (variant) => {
      if (!variant) {
        if (hasBagSize) setPrice(bagSizeRange);
        setButtonState(atcButton, atcLabel, {
          disabled: false,
          awaiting: true,
          label: addLabel,
        });
        return;
      }

      setPrice(formatPackPriceWithPerPound(variant, product));
      if (window.showroomOffrefy && typeof window.showroomOffrefy.updateTile === "function") {
        window.showroomOffrefy.updateTile(root);
      }
      const available = variant.available !== false;
      setButtonState(atcButton, atcLabel, {
        disabled: !available,
        awaiting: false,
        label: available ? addLabel : soldOutLabel,
      });
    };

    const nudgeVariantPicker = () => {
      if (!selectEl) return;
      selectEl.classList.remove("is-popping");
      void selectEl.offsetWidth;
      selectEl.classList.add("is-popping");
      selectEl.focus({ preventScroll: true });
      selectEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      const clearPop = () => selectEl.classList.remove("is-popping");
      selectEl.addEventListener("animationend", clearPop, { once: true });
    };

    if (selectEl) {
      Array.from(selectEl.options).forEach((option) => {
        if (!option.value) return;
        const variant = getVariant(product, option.value);
        const sizeLabel = variant && (variant.option1 || (variant.options && variant.options[0]));
        const raw = sizeLabel || option.textContent || "";
        option.textContent = String(raw)
          .replace(/\s*\$[\d,.]+(?:\s*\([^)]+\))?/g, "")
          .replace(/\s*\(\s*\$[^)]+\)/g, "")
          .trim() || option.textContent;
      });
      selectEl.addEventListener("change", () => {
        setError("");
        applyVariant(getVariant(product, selectEl.value));
      });
      if (selectEl.value) {
        applyVariant(getVariant(product, selectEl.value));
      }
    }

    root.querySelectorAll("[data-showroom-qty-step]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        if (!qtyInput) return;
        const sign = Number(button.getAttribute("data-showroom-qty-step") || 0);
        if (window.showroomQuantityPack && window.showroomQuantityPack.isPackInput(qtyInput)) {
          window.showroomQuantityPack.adjustByButton(qtyInput, sign);
        } else {
          const next = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + sign);
          qtyInput.value = String(next);
        }
        if (window.showroomOffrefy && typeof window.showroomOffrefy.updateTile === "function") {
          window.showroomOffrefy.updateTile(root);
        }
      });
    });

    if (form) {
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        const variantId = selectEl ? selectEl.value : form.querySelector('input[name="id"]')?.value;
        const variant = getVariant(product, variantId);
        if (!variant) {
          nudgeVariantPicker();
          return;
        }
        if (variant.available === false) return;

        const formData = new FormData(form);
        const theme = window.theme || {};
        const modalCart = theme.settings && theme.settings.cartType === "modal";
        const routes = theme.routes || {};

        if (modalCart) {
          formData.append("sections", "cart-items,cart-footer,cart-item-count");
          formData.append("sections_url", window.location.pathname);
        }

        setError("");
        setButtonState(atcButton, atcLabel, { disabled: true, loading: true, label: addingLabel });

        fetch(routes.cart_add_url || "/cart/add.js", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/javascript",
          },
          body: formData,
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              const message = data.description || data.message || (theme.strings && theme.strings.cartError) || "Unable to add to cart";
              setError(typeof message === "string" ? message : addLabel);
              applyVariant(variant);
              document.body.dispatchEvent(
                new CustomEvent("shapes:cart:adderror", {
                  detail: {
                    source: "product-form",
                    sourceId: form.id,
                    variantId: Number(formData.get("id")),
                    errors: data.errors || data.description,
                    message,
                  },
                })
              );
              return;
            }

            applyVariant(variant);
            if (modalCart) {
              document.body.dispatchEvent(
                new CustomEvent("shapes:cart:afteradditem", {
                  bubbles: true,
                  detail: { response: data },
                })
              );
            }
          })
          .catch((error) => {
            console.error(error);
            applyVariant(variant);
          });
      });
    }
  };

  const getActiveSort = () => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("sort_by");
    if (fromUrl) return fromUrl;
    const hidden = document.getElementById("Facet-Sort-By");
    return hidden ? hidden.value : "";
  };

  const sortPriceFromItem = (item, descending) => {
    const el = item.querySelector("[data-sort-price-min]") || item;
    const min = Number(el.getAttribute("data-sort-price-min") || 0);
    const max = Number(el.getAttribute("data-sort-price-max") || min);
    return descending ? max : min;
  };

  const sortResultsByDisplayedPrice = (scope) => {
    const sortBy = getActiveSort();
    if (sortBy !== "price-ascending" && sortBy !== "price-descending") return;

    const root = scope && scope.querySelectorAll ? scope : document;
    const lists = root.querySelectorAll("#facets-results ul, .showroom-collection-grid__list");
    const descending = sortBy === "price-descending";
    const seen = new Set();

    lists.forEach((list) => {
      if (!list || seen.has(list)) return;
      seen.add(list);
      const items = Array.from(list.children).filter((el) => el.tagName === "LI");
      if (items.length < 2) return;
      items
        .sort((a, b) => {
          const priceA = sortPriceFromItem(a, descending);
          const priceB = sortPriceFromItem(b, descending);
          return descending ? priceB - priceA : priceA - priceB;
        })
        .forEach((item) => list.appendChild(item));
    });
  };

  const applyCollectionView = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    let isList = false;
    try {
      isList = window.localStorage.getItem("showroomCollectionView") === "list";
    } catch (e) {}
    root.querySelectorAll(".showroom-collection-grid__list").forEach((list) => {
      list.classList.toggle("is-list-view", isList);
      list.classList.toggle("is-grid-view", !isList);
    });
  };

  const selectedOriginCountries = () => {
    const params = new URLSearchParams(window.location.search);
    return params
      .getAll("filter.p.m.custom.country")
      .concat(params.getAll("sr_origin"))
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
  };

  const syncOriginCountryCheckboxes = () => {
    const selected = selectedOriginCountries();
    const inputs = document.querySelectorAll(
      '#left-drawer-slot input[name="sr_origin"], #left-drawer-slot input[name="filter.p.m.custom.country"], input[name="sr_origin"], input[name="filter.p.m.custom.country"]'
    );
    inputs.forEach((input) => {
      const value = String(input.value || input.getAttribute("data-origin-country-option") || "")
        .trim()
        .toLowerCase();
      input.checked = selected.indexOf(value) !== -1;
    });

    const hasCountry = selected.length > 0;
    const hasNative = !!document.querySelector(
      '#left-drawer-slot input[name^="filter."]:checked, .sidebar-filters input[name^="filter."]:checked'
    );
    const showClear = hasCountry || hasNative;
    document.querySelectorAll(".js-showroom-filters-clear").forEach((btn) => {
      btn.hidden = !showClear;
    });
    document.querySelectorAll(".js-showroom-filters-footer").forEach((footer) => {
      footer.classList.toggle("sidebar-filters__footer--has-clear", showClear);
    });
  };

  const applyOriginCountryFilter = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    syncOriginCountryCheckboxes();
    const lists = root.querySelectorAll("#facets-results .js-showroom-collection-list, .showroom-collection-grid__list");
    if (!lists.length) return;

    const selected = selectedOriginCountries();
    lists.forEach((list) => {
      const items = Array.from(list.children).filter((el) => el.tagName === "LI");
      if (!items.length) return;
      let visible = 0;
      items.forEach((item) => {
        const country = String(item.getAttribute("data-origin-country") || "").trim().toLowerCase();
        const match = !selected.length || (country && selected.indexOf(country) !== -1);
        item.hidden = !match;
        item.style.display = match ? "" : "none";
        if (match) visible += 1;
      });
      let empty = list.parentNode && list.parentNode.querySelector(".js-showroom-origin-empty");
      if (!empty && list.parentNode) {
        empty = document.createElement("p");
        empty.className = "text-center js-showroom-origin-empty";
        empty.hidden = true;
        empty.textContent = "Sorry, there are no products in this collection";
        list.parentNode.appendChild(empty);
      }
      if (empty) empty.hidden = visible !== 0;
    });
  };

  const initAll = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll(TILE_SELECTOR).forEach(initTile);
    sortResultsByDisplayedPrice(root);
    applyCollectionView(root);
    applyOriginCountryFilter(root);
  };

  const watchFilterableResults = () => {
    const container = document.getElementById("facets-filterable");
    if (!container || container.dataset.showroomTilesObserved === "true") return;
    container.dataset.showroomTilesObserved = "true";
    new MutationObserver(() => {
      requestAnimationFrame(() => initAll(container));
      window.setTimeout(() => applyOriginCountryFilter(document), 50);
      window.setTimeout(() => applyOriginCountryFilter(document), 350);
    }).observe(container, { childList: true });
  };

  const watchOriginFilterDrawer = () => {
    const drawer = document.getElementById("left-drawer-slot");
    if (!drawer || drawer.dataset.showroomOriginObserved === "true") return;
    drawer.dataset.showroomOriginObserved = "true";
    new MutationObserver(() => {
      window.setTimeout(() => syncOriginCountryCheckboxes(), 0);
      window.setTimeout(() => syncOriginCountryCheckboxes(), 50);
    }).observe(drawer, { childList: true, subtree: true });
  };

  const bindOriginFilterRefresh = () => {
    if (document.documentElement.dataset.showroomOriginFilterBound === "true") return;
    document.documentElement.dataset.showroomOriginFilterBound = "true";
    const refresh = () => {
      window.setTimeout(() => applyOriginCountryFilter(document), 0);
      window.setTimeout(() => applyOriginCountryFilter(document), 50);
      window.setTimeout(() => applyOriginCountryFilter(document), 350);
    };
    document.addEventListener("click", (event) => {
      const target = event.target;
      if (!target || !target.closest) return;
      if (target.closest(".sidebar-filters__btn--apply") || target.closest(".sidebar-filters__btn--clear")) {
        refresh();
      }
    });
    window.addEventListener("popstate", refresh);
  };

  const start = () => {
    initAll(document);
    watchFilterableResults();
    watchOriginFilterDrawer();
    bindOriginFilterRefresh();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
    watchFilterableResults();
    watchOriginFilterDrawer();
  });
  document.addEventListener("shapes:section:hasmutated", (event) => {
    initAll(event.target);
  });
})();
